// main.js — AI 对话 bootstrap (simple stable)
import * as roleFacade from "./interaction/role-facade.js";
import * as sessionFacade from "./interaction/session-facade.js";
import * as messageFacade from "./interaction/message-facade.js";
import * as artifactFacade from "./interaction/artifact-facade.js";
import * as runFacade from "./interaction/run-facade.js";
import * as memoryFacade from "./interaction/memory-facade.js";
import { setActiveSession } from "./interaction/message-facade.js";
import { submitFeedback, fetchInstalledSkills, fetchToolCapabilities } from "./transport/api/agent-api.js";
import { resetMessageForRetry } from "./transport/api/message-api.js";
import { messageStore } from "./interaction/stores/message-store.js";
import { sessionStore } from "./interaction/stores/session-store.js";
import { artifactStore } from "./interaction/stores/artifact-store.js";
import { runStore } from "./interaction/stores/run-store.js";
import { memoryStore } from "./interaction/stores/memory-store.js";
import { canvasStore } from "./interaction/stores/canvas-store.js";
import * as roleApi from "./transport/api/role-api.js";
import { estimateMessagesTokens } from "./utils/token-estimator.js";
import * as rolePicker from "./presentation/role-picker.js";
import { renderRoleCards as renderRoleCardsView, renderRoleError as renderRoleErrorView, renderRoleLoading as renderRoleLoadingView } from "./presentation/role-cards.js";
import { renderSessionList as renderSessionListView, renderSessionListError as renderSessionListErrorView, renderSessionListLoading as renderSessionListLoadingView } from "./presentation/session-list.js";
import { renderAuthRequiredStream, renderSessionLoadFailure } from "./presentation/stream-states.js";
import { renderAttachmentPreview as renderAttachmentPreviewView } from "./presentation/attachment-preview.js";
import { createAttachmentController } from "./interaction/attachment-controller.js";
import { createCustomRoleController } from "./interaction/custom-role-controller.js";
import { createMessageContentView } from "./presentation/message-content.js";
import {
  createToastElement,
  renderFeedbackAction,
  renderHeaderBadge,
  renderTemporaryCopyAction,
} from "./presentation/message-action-controls.js";
import { closeModalDialog, openModalDialog } from "../interaction/ui/modal-dialog.js";
import { createCrossTabSessionSync } from "./interaction/cross-tab-session-sync.js";
import { createSessionOpeningController } from "./interaction/session-opening-controller.js";
import { createSessionListController } from "./interaction/session-list-controller.js";
import { createWorkbenchViewController } from "./interaction/workbench-view-controller.js";
import { createMessageActionController } from "./interaction/message-action-controller.js";
import { createMessageSubmissionController } from "./interaction/message-submission-controller.js";
import { createStreamLifecycleController } from "./interaction/stream-lifecycle-controller.js";
import { createConversationViewController } from "./presentation/conversation-view-controller.js";
import { createInputSurfaceController } from "./presentation/input-surface-controller.js";
import { createWorkbenchActionController } from "./interaction/workbench-action-controller.js";
import { createPageShellController } from "./presentation/page-shell-controller.js";
import { createWorkbenchDataController } from "./interaction/workbench-data-controller.js";
import { createWorkbenchRenderer } from "./presentation/workbench-renderer.js";
import { formatAgentStateLabel, getToolMeta } from "./presentation/workbench-labels.js";

const $ = id => document.getElementById(id);

// ── DOM refs ──
const elSessionList = $("aichat-session-list");
const elStream    = $("aichat-stream");
const elRoleCards = $("aichat-role-cards");
const elInputCard = $("aichat-input-card");
const elTextarea  = $("aichat-textarea");
const elCounter  = $("aichat-counter");
const elSendBtn  = $("aichat-send-btn");
const elStopBtn  = $("aichat-stop-btn");
const elNewBtn   = $("aichat-new-btn");
// T5: token 计数显示元素
const elTokenCounter = $("aichat-token-counter");
// T8: 新消息浮动按钮
const elNewMsgBtn = $("aichat-new-msg-btn");
const elArtifactList = $("aichat-artifact-list");
const elCanvasEmpty = $("aichat-canvas-empty");
const elSkillsList = $("aichat-skills-list");
const elSkillsItems = $("aichat-skills-items");
const elToolsItems = $("aichat-tools-items");
const elMemoryList = $("aichat-memory-list");
const elWorkbench = $("aichat-workbench");
const elWorkbenchToggle = $("aichat-workbench-toggle");
const elWorkbenchResizer = $("aichat-workbench-resizer");
const elSidebar = $("aichat-sidebar");
const elSidebarScrim = $("aichat-sidebar-scrim");
const elMobileSidebarToggle = $("aichat-mobile-sidebar-toggle");

// ── P1+P2: 附件 + 工具 ──
const elBtnPlus = $("aichat-btn-plus");
const elBtnTools = $("aichat-btn-tools");
const elFileInput = $("aichat-file-input");
const elAttachmentsPreview = $("aichat-attachments-preview");
const elInputArea = $("aichat-input-area");
const elDragOverlay = $("aichat-drag-overlay");
let localStreamingSessionId = null;

// ── role picker ──
let roles = [];
let sessionListStatus = "ACTIVE";

const pageShellController = createPageShellController({
  elements: {
    sidebar: elSidebar,
    sidebarScrim: elSidebarScrim,
    mobileSidebarToggle: elMobileSidebarToggle,
    workbench: elWorkbench,
    workbenchResizer: elWorkbenchResizer,
  },
  getElement: $,
  getRoleNameByCode,
  updateMarkExtractButton,
  createToastElement,
  renderHeaderBadge,
});
const {
  closeMobileSidebar,
  showToast,
  friendlyAgentError,
  copyToClipboard,
  copyCodeBlock,
  showConfirm,
  openLightbox,
  getAvatarClass,
  getAvatarLetter,
  updateHeader,
  updateInputVisibility,
} = pageShellController;
pageShellController.bind();

let inputSurfaceController = null;
const attachmentController = createAttachmentController({
  onStateChange: () => {
    inputSurfaceController?.sync();
  },
  onUploadError: (message) => showToast(message, "error"),
});

function showRolePicker(roleList) {
  roles = roleList || [];
  rolePicker.show(roles);
}

const crossTabSessionSync = createCrossTabSessionSync({
  sessionStore,
  messageStore,
  sessionFacade,
  updateTokenCounter,
  getLocalStreamingSessionId: () => localStreamingSessionId,
});
const {
  publishAgentEvent,
  requestStreamingSnapshot,
  syncRemoteSession,
} = crossTabSessionSync;

// ── Role cards rendering ──
// 区分公共角色(userId=null) 和用户私有角色(userId=当前用户)
function renderRoleCards(roleList) {
  roles = roleList || [];
  renderRoleCardsView(elRoleCards, roles);
}

function renderRoleLoading() {
  renderRoleLoadingView(elRoleCards);
}

function renderRoleError(message = "角色加载失败") {
  renderRoleErrorView(elRoleCards, message);
}

function hideRoleCards() {
  if (elRoleCards) elRoleCards.hidden = true;
  if (elStream) elStream.hidden = false;
  workbenchController.syncVisibility();
  updateInputVisibility(true);
}

function showRoleCards() {
  if (elRoleCards) elRoleCards.hidden = false;
  if (elStream) elStream.hidden = true;
  workbenchController.setVisible(false);
  updateInputVisibility(false);
  updateHeader(null);
}

async function refreshRoles() {
  const loadedRoles = await roleFacade.loadRoles();
  renderRoleCards(loadedRoles);
}

const customRoleController = createCustomRoleController({
  roleFacade,
  getRoles: () => roles,
  refreshRoles,
  showToast,
  showConfirm,
  openModal: openModalDialog,
  closeModal: closeModalDialog,
});
const {
  openCustomRoleModal,
  closeCustomRoleModal,
  saveCustomRole,
  deleteCustomRole,
} = customRoleController;

// ── Session list rendering ──
function renderSessionList() {
  const { sessions, currentSession } = sessionStore.getSessionState();
  renderSessionListView(elSessionList, {
    sessions,
    currentSession,
    status: sessionListStatus,
    keyword: $("aichat-session-search")?.value?.trim(),
    getRoleName: getRoleNameByCode,
    getAvatarClass,
    getAvatarLetter,
  });
}

function renderSessionListLoading() {
  renderSessionListLoadingView(elSessionList);
}

function renderSessionListError() {
  renderSessionListErrorView(elSessionList);
}

function renderMessageContent(group, message) {
  const isUser = message.role === 'USER';
  group.replaceChildren(createMessageContentView(message, {
    avatarClass: isUser ? 'aichat-avatar--user' : getAvatarClass(window._currentRoleCode),
    avatarLetter: isUser ? '我' : getAvatarLetter(window._currentRoleCode, ''),
    getToolMeta,
    formatAgentStateLabel,
    expandedAgentActivity: messageActionController.isAgentActivityExpanded(message.id),
  }));
}

// 工作台内容感知显隐：素材/任务/画布/技能四者皆空时隐藏工作台，避免首次进入会话显示空内容。
// 页面入口只装配数据控制器与 presentation renderer，角色选择页面不显示工作台。
let workbenchController = null;
const workbenchRenderer = createWorkbenchRenderer({
  elements: {
    artifactList: elArtifactList, canvasEmpty: elCanvasEmpty, skillsItems: elSkillsItems,
    toolsItems: elToolsItems, memoryList: elMemoryList,
  },
  syncVisibility: () => workbenchController?.syncVisibility(),
});
const workbenchDataController = createWorkbenchDataController({
  fetchToolCapabilities,
  fetchInstalledSkills,
  renderTools: workbenchRenderer.renderTools,
  renderSkills: workbenchRenderer.renderSkills,
  syncVisibility: () => workbenchController?.syncVisibility(),
});
const workbenchRenderers = {
  ...workbenchRenderer,
  renderSkills: () => workbenchRenderer.renderSkills(workbenchDataController.getSkillRenderState()),
  renderTools: () => workbenchRenderer.renderTools(workbenchDataController.getToolRenderState()),
};
workbenchController = createWorkbenchViewController({
  elements: {
    artifactList: elArtifactList, canvas: elCanvasEmpty, skillsList: elSkillsList,
    skillsItems: elSkillsItems, toolsItems: elToolsItems, memoryList: elMemoryList,
    workbench: elWorkbench, workbenchToggle: elWorkbenchToggle,
  },
  stores: { artifactStore, runStore, memoryStore, canvasStore, sessionStore },
  getWorkspaceState: () => {
    const artifactState = artifactStore.getArtifactState();
    const runState = runStore.getRunState();
    const memoryState = memoryStore.getMemoryState();
    return {
      artifacts: artifactState.artifacts.length, runs: runState.runs.length,
      canvasArtifacts: canvasStore.getCanvasState().artifacts.length,
      memoryItems: memoryState.items.length, memoryLoading: memoryState.loading, memoryError: memoryState.error,
      artifactsLoading: artifactState.loading, artifactsError: artifactState.error,
      runsLoading: runState.loading, runsError: runState.error,
      ...workbenchDataController.getWorkspaceState(),
    };
  },
  getRoleCardsVisible: () => Boolean(elRoleCards && !elRoleCards.hidden),
  renderers: workbenchRenderers,
  loadMemoryItems: () => memoryFacade.loadMemoryItems(),
  initialCollapsed: localStorage.getItem('aichat_workbench_collapsed') !== 'false',
  onCollapsedChange: (collapsed) => localStorage.setItem('aichat_workbench_collapsed', String(collapsed)),
});
workbenchController.bind();

inputSurfaceController = createInputSurfaceController({
  elements: {
    textarea: elTextarea,
    counter: elCounter,
    sendButton: elSendBtn,
    plusButton: elBtnPlus,
    fileInput: elFileInput,
    attachmentsPreview: elAttachmentsPreview,
    inputArea: elInputArea,
    dragOverlay: elDragOverlay,
    toolsButton: elBtnTools,
    toolsItems: elToolsItems,
    skillsItems: elSkillsItems,
  },
  attachmentController,
  renderAttachmentPreview: renderAttachmentPreviewView,
  onRetryTools: workbenchDataController.loadToolCapabilities,
  onRetrySkills: workbenchDataController.loadInstalledSkills,
  getCurrentRoleCode: () => window._currentRoleCode,
});
const { updateCounter, renderToolsAlwaysOn } = inputSurfaceController;
inputSurfaceController.bind();
renderToolsAlwaysOn();

const conversationViewController = createConversationViewController({
  stream: elStream,
  newMessageButton: elNewMsgBtn,
  roleCards: elRoleCards,
  getMessages: () => messageStore.getMessageState().messages,
  getCurrentSession: () => sessionStore.getSessionState().currentSession,
  renderMessageContent,
  setInputVisibility: updateInputVisibility,
});
const {
  renderMessages,
  resetScrollState,
  showSessionLoading,
} = conversationViewController;
conversationViewController.bind();

const workbenchActionController = createWorkbenchActionController({
  artifactList: elArtifactList,
  canvasEmpty: elCanvasEmpty,
  memoryList: elMemoryList,
  textarea: elTextarea,
  sessionStore,
  artifactFacade,
  runFacade,
  memoryFacade,
  artifactStore,
  canvasStore,
  workbenchController,
});
workbenchActionController.bind();

// ── T5: 上下文 token 计数显示 ──
// 依据当前 messageStore 实时估算 + session-facade 提供的 maxTokens
// 颜色分级: 灰色 <80% / 橙色 80%-100% / 红色 >100% (此时后端已裁剪最早消息)
function formatTokenCount(n) {
  if (n >= 1000) return (n / 1000).toFixed(1) + "k";
  return String(n);
}

function updateTokenCounter() {
  if (!elTokenCounter) return;
  const ctx = sessionFacade.getCurrentContext();
  // 无会话 / 无 maxTokens 时隐藏
  if (!ctx || !ctx.maxTokens) {
    elTokenCounter.hidden = true;
    elTokenCounter.textContent = "";
    return;
  }
  const { messages } = messageStore.getMessageState();
  const estimated = estimateMessagesTokens(messages);
  const max = Number(ctx.maxTokens) || 128000;
  const ratio = estimated / max;
  const truncated = ctx.truncated === true || estimated >= max;

  let cls = "aichat-token-counter";
  if (truncated) cls += " aichat-token-counter--danger aichat-token-counter--truncated";
  else if (ratio >= 0.8) cls += " aichat-token-counter--warn";
  elTokenCounter.className = cls;
  elTokenCounter.hidden = false;
  elTokenCounter.textContent = `tokens: ${formatTokenCount(estimated)} / ${formatTokenCount(max)}`;
}

function renderAuthRequired() {
  if (elRoleCards) elRoleCards.hidden = true;
  if (elStream) elStream.hidden = false;
  workbenchController.setVisible(false);
  updateInputVisibility(false);
  renderAuthRequiredStream(elStream);
  elTextarea.disabled = true;
  elSendBtn.disabled = true;
  elNewBtn.disabled = true;
}

// ── Stream UI, recovery and page lifecycle ──
const streamLifecycleController = createStreamLifecycleController({
  elements: { sendButton: elSendBtn, stopButton: elStopBtn },
  onStreamStateChange: messageFacade.onStreamStateChange,
  stopActiveStream: messageFacade.stopActiveStream,
  sessionStore,
  artifactFacade,
  runFacade,
  getLastPersistedMessageId: messageFacade.getLastPersistedMessageId,
  syncMessagesAfter: messageFacade.syncMessagesAfter,
  updateTokenCounter,
  publishAgentEvent,
  // AFV-006：后台增量同步失败时提醒用户，避免新回复静默缺失。
  onSyncError: () => showToast('消息同步失败，最新回复可能未显示，可刷新页面查看', 'error'),
});
streamLifecycleController.bind();

// ── Message action delegation ──
let messageSubmissionController;
const messageActionController = createMessageActionController({
  stream: elStream,
  messageStore,
  sessionStore,
  messageFacade,
  memoryFacade,
  artifactFacade,
  submitFeedback,
  copyToClipboard,
  copyCodeBlock,
  openLightbox,
  renderMessageContent,
  renderTemporaryCopyAction,
  renderFeedbackAction,
  showToast,
  friendlyAgentError,
  retryFailedMessage: async ({ messageId, mode }) => {
    const currentSession = sessionStore.getSessionState().currentSession;
    const target = messageStore.getMessageState().messages.find((message) => String(message.id) === messageId);
    const userMessage = target?.parentId
      ? messageStore.getMessageState().messages.find((message) => String(message.id) === String(target.parentId) && message.role === 'USER')
      : null;
    if (!currentSession || !target || !userMessage || !messageSubmissionController) return;
    const edited = mode === 'edit'
      ? window.prompt('修改原问题后重新运行：', userMessage.content || '')
      : userMessage.content;
    if (edited == null) return;
    const content = String(edited).trim();
    if (!content) return void showToast('问题内容不能为空', 'error');
    if (mode === 'delete' && !window.confirm('删除本轮失败消息，并使用原问题重新运行？')) return;
    try {
      // AFV-002：本地临时消息（tmp- id）未落库，跳过服务端 reset 直接本地重发。
      let retryAttachments = userMessage.attachments || [];
      if (/^\d+$/.test(String(userMessage.id))) {
        const result = await resetMessageForRetry(currentSession.id, Number(userMessage.id));
        retryAttachments = result.message?.attachments || [];
      }
      messageStore.removeMessage(target.id);
      messageStore.removeMessage(userMessage.id);
      await messageSubmissionController.send(content, retryAttachments);
    } catch (error) {
      showToast(`重试失败：${error.message || '请求失败'}`, 'error');
    }
  },
});
messageActionController.bind();

// ── New session ──
elNewBtn.addEventListener("click", async () => {
  try {
    const loadedRoles = await roleFacade.loadRoles();
    showRolePicker(loadedRoles);
  } catch(e) { console.error(e); }
});

// ── Role picker events ──
rolePicker.init({ onPick: createSessionWithRole });

// ── Role card events ──
elRoleCards.addEventListener("click", async e => {
  const retryRoles = e.target.closest("[data-action='retry-load-roles']");
  if (retryRoles) {
    renderRoleLoading();
    try {
      const loadedRoles = await roleFacade.loadRoles();
      renderRoleCards(loadedRoles);
      renderSessionList();
    } catch (error) {
      if (error?.status === 401) return renderAuthRequired();
      renderRoleError();
    }
    return;
  }
  // 优先:删除按钮
  const delBtn = e.target.closest("[data-delete-custom]");
  if (delBtn) {
    e.stopPropagation();
    deleteCustomRole(delBtn.dataset.deleteCustom);
    return;
  }
  // 优先:编辑按钮
  const editBtn = e.target.closest("[data-edit-custom]");
  if (editBtn) {
    e.stopPropagation();
    const code = editBtn.dataset.editCustom;
    const role = roles.find(r => r.code === code);
    if (role) openCustomRoleModal(role);
    return;
  }
  // "创建自定义"卡片
  const addCard = e.target.closest('[data-action="create-custom"]');
  if (addCard) {
    openCustomRoleModal();
    return;
  }
  // 普通卡片:进入会话
  const card = e.target.closest(".aichat-role-card");
  if (!card) return;
  // 私有角色上右键(此处略) / 长按也可以编辑,目前编辑通过右上角⚙
  const code = card.dataset.roleCode;
  if (code) await createSessionWithRole(code);
});

const sessionOpeningController = createSessionOpeningController({
  sessionFacade,
  sessionStore,
  messageStore,
  artifactFacade,
  runFacade,
  canvasStore,
  setActiveSession,
  requestStreamingSnapshot,
  loadInstalledSkills: workbenchDataController.loadInstalledSkills,
  renderToolsAlwaysOn,
  setCurrentRoleCode: (code) => { window._currentRoleCode = code; },
  hideRoleCards,
  showSessionLoading,
  updateHeader,
  renderSessionList,
  renderMessages,
  updateInputVisibility,
  updateTokenCounter,
  resetScrollState,
  closeMobileSidebar,
  renderSessionLoadFailure: () => renderSessionLoadFailure(elStream),
  showToast,
  friendlyAgentError,
});

function createSessionWithRole(code) {
  const roleCards = Array.from(elRoleCards.querySelectorAll("[data-role-code]"))
    .filter((card) => card.dataset.roleCode === code);
  return sessionOpeningController.createSessionWithRole(code, { roleCards, newSessionButton: elNewBtn });
}

const sessionListController = createSessionListController({
  sessionListElement: elSessionList,
  searchInput: $("aichat-session-search"),
  tabsElement: $("aichat-session-tabs"),
  sessionFacade, sessionStore, messageStore, artifactFacade, runFacade, sessionOpeningController,
  getStatus: () => sessionListStatus,
  setStatus: (status) => { sessionListStatus = status; },
  renderLoading: renderSessionListLoading,
  renderError: renderSessionListError,
  renderMessages, updateTokenCounter, showRoleCards, showToast, showConfirm,
});

// ── Session list events ──
sessionListController.bind();

// ── Send message ──
messageSubmissionController = createMessageSubmissionController({
  elements: { textarea: elTextarea, sendButton: elSendBtn },
  attachmentController,
  messageStore,
  sessionStore,
  messageFacade,
  canvasStore,
  memoryFacade,
  artifactFacade,
  renderMessages,
  updateCounter,
  showToast,
  friendlyAgentError,
  publishAgentEvent,
  syncRemoteSession,
  getLocalStreamingSessionId: () => localStreamingSessionId,
  setLocalStreamingSessionId: (sessionId) => { localStreamingSessionId = sessionId; },
});
messageSubmissionController.bind();

// ── Init ──
sessionStore.subscribe(renderSessionList);
messageStore.subscribe(renderMessages);
// 三个工作台 store 统一走控制器渲染，避免争抢同一 DOM 容器造成渲染竞态。
artifactStore.subscribe(() => workbenchController.renderActivePanel());
memoryStore.subscribe(() => { workbenchController.renderActivePanel(); workbenchController.syncVisibility(); });
canvasStore.subscribe(() => workbenchController.renderActivePanel());
runStore.subscribe(() => workbenchController.renderActivePanel());
// T5: 消息状态变化时同步刷新 token 计数(包括流式追加)
messageStore.subscribe(updateTokenCounter);

// ── T2: 会话搜索 (服务端) + 活跃/已归档 切换 ──


// ── Header 操作 ──
// "清空会话" 实质是归档当前会话（保留管理后台可见性），等同于列表项的归档操作。
// 后端没有"清空消息保留会话壳"的 API；这里复用 archive 流程，避免假操作误导用户。
/**
 * 同步「标记沉淀」按钮视觉态（高亮 / 文案 / aria-pressed）。
 * @param {boolean} marked
 */
function updateMarkExtractButton(marked) {
  const btn = $("aichat-btn-mark-extract");
  if (!btn) return;
  const on = Boolean(marked);
  btn.classList.toggle("is-marked", on);
  btn.setAttribute("aria-pressed", on ? "true" : "false");
  btn.title = on
    ? "已标记：本会话优先进入技能提炼（再点可取消）"
    : "标记本会话值得沉淀为技能（供经验提取）";
  const label = btn.querySelector("span");
  if (label) label.textContent = on ? "已标记沉淀" : "标记沉淀";
}

$("aichat-btn-mark-extract")?.addEventListener("click", async () => {
  const session = sessionStore.getSessionState().currentSession;
  if (!session?.id) {
    showToast("请先选择或创建会话");
    return;
  }
  const btn = $("aichat-btn-mark-extract");
  const currentlyMarked = Boolean(session.skillExtractMark);
  const nextMark = !currentlyMarked;
  if (btn) btn.disabled = true;
  try {
    const res = await fetch(`/api/agent/sessions/${session.id}/skill-extract-mark`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mark: nextMark }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || `HTTP ${res.status}`);
    }
    const data = await res.json().catch(() => ({}));
    const marked = Number(data.skill_extract_mark) === 1 || data.skill_extract_mark === true || nextMark;
    const patched = { ...session, skillExtractMark: marked };
    sessionStore.setCurrentSession(patched);
    sessionStore.upsertSession(patched);
    updateMarkExtractButton(marked);
    showToast(marked
      ? "已标记：本会话将优先进入技能提炼"
      : "已取消标记：本会话不再优先沉淀");
  } catch (err) {
    showToast(`标记失败：${err.message || err}`);
  } finally {
    if (btn) btn.disabled = false;
  }
});

$("aichat-btn-clear")?.addEventListener("click", async () => {
  const currentSession = sessionStore.getSessionState().currentSession;
  if (!currentSession) {
    showToast("当前没有可清空的会话");
    return;
  }
  const ok = await showConfirm("清空当前会话", "将归档当前会话的所有消息并切换到新会话。此操作不可恢复。");
  if (!ok) return;
  try {
    messageFacade.stopActiveStream?.();
    await sessionFacade.archiveSession(currentSession.id);
    sessionFacade.clearCurrentContext();
    messageStore.setMessages([]);
    renderMessages();
    reloadSessionList();
    // P0-fix: 恢复到角色卡片屏，与归档按钮 handler 对齐
    showRoleCards();
    showToast("会话已清空");
  } catch (e) {
    console.error("[aichat] clear session failed:", e);
    showToast(e.message || "清空会话失败", "error");
  }
});
$("aichat-btn-settings")?.addEventListener("click", () => showToast("设置面板开发中"));

function getRoleNameByCode(code) {
  if (!code) return "";
  const role = roles.find(r => r.code === code);
  if (role?.name) return role.name;
  const builtInNames = {
    selector_king: "选品专家",
    listing_scorer: "Listing 评分专家",
    ceo_maomao: "贸贸",
  };
  return builtInNames[code] || (String(code).startsWith("user_") ? "自定义角色" : "通用助手");
}

async function initDemoBar() {
  const bar = $("aichat-demo-bar");
  const chips = $("aichat-demo-chips");
  if (!bar || !chips) return;
  try {
    const res = await fetch("/api/demo/status", { credentials: "same-origin" });
    if (!res.ok) return;
    const data = await res.json();
    if (!data?.demoMode || !Array.isArray(data.scenarios) || data.scenarios.length === 0) return;
    chips.replaceChildren();
    for (const scenario of data.scenarios) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "aichat-demo-chip";
      btn.textContent = scenario.label || scenario.id;
      btn.title = scenario.prompt || "";
      btn.addEventListener("click", () => {
        const prompt = String(scenario.prompt || "").trim();
        if (!prompt) return;
        if (typeof messageSubmissionController?.send === "function") {
          messageSubmissionController.send(prompt).catch(() => {});
          return;
        }
        if (elTextarea) {
          elTextarea.value = prompt;
          elTextarea.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });
      chips.append(btn);
    }
    bar.hidden = false;
  } catch (err) {
    console.warn("[aichat] demo status unavailable", err);
  }
}

async function init() {
  renderRoleLoading();
  renderSessionListLoading();
  const [roleResult, sessionResult] = await Promise.allSettled([
    roleFacade.loadRoles(),
    sessionFacade.loadSessions(),
  ]);
  const authError = [roleResult, sessionResult].find((result) => result.status === "rejected" && result.reason?.status === 401);
  if (authError) return renderAuthRequired();

  if (roleResult.status === "fulfilled") {
    renderRoleCards(roleResult.value);
  } else {
    console.error("[aichat] load roles failed:", roleResult.reason);
    renderRoleError();
  }
  if (sessionResult.status === "fulfilled") {
    renderSessionList();
  } else {
    console.error("[aichat] load sessions failed:", sessionResult.reason);
    renderSessionListError();
  }
  showRoleCards();
  workbenchDataController.loadInstalledSkills();
  workbenchDataController.loadToolCapabilities();
  memoryFacade.loadMemoryItems();
  initDemoBar();
}

// ── 自定义角色模态框事件绑定 ──
document.getElementById("aichat-custom-role-close")?.addEventListener("click", closeCustomRoleModal);
document.getElementById("aichat-custom-role-cancel-btn")?.addEventListener("click", closeCustomRoleModal);
document.getElementById("aichat-custom-role-form")?.addEventListener("submit", (e) => {
  e.preventDefault();
  saveCustomRole();
});

init();
